import { useState } from 'react';
import { X } from 'lucide-react';
import { isOwnerRole, isRoleAssignable } from './constants';

// Papel padrão do convite: o de system_key === 'member' se atribuível; senão
// "Editor" por nome (bundle antigo); senão vazio. Mesma regra de
// UsersPage.jsx original — preservada para não regredir o comportamento já
// validado (achado O-3 / D4 de .claude/rbac/decisoes-dono.md).
function defaultInviteRoleId(roles) {
  const member = roles.find((r) => r.system_key === 'member');
  if (member && isRoleAssignable(member)) return member.id;
  return roles.find((r) => r.name === 'Editor')?.id ?? '';
}

export default function InviteUserModal({ roles, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState(defaultInviteRoleId(roles));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const generatePassword = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let p = '';
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPassword(p);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await onCreate({ name: name.trim(), email: email.trim().toLowerCase(), password, role_id: Number(roleId) });
      onClose();
    } catch (e2) {
      setErr(e2.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card rounded-xl border border-border shadow-2xl w-[460px]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <h3 className="text-sm font-semibold text-text-primary">Convidar novo usuário</h3>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Nome</label>
            <input
              required autoFocus value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">E-mail</label>
            <input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Senha temporária</label>
            <div className="flex gap-2">
              <input
                required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50 font-mono"
              />
              <button
                type="button" onClick={generatePassword}
                className="px-3 text-xs text-accent border border-accent/40 rounded-lg hover:bg-accent/10 transition-colors"
              >
                Gerar
              </button>
            </div>
            <p className="text-[11px] text-text-secondary mt-1">Compartilhe com o usuário para o primeiro acesso.</p>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Grupo</label>
            <select
              value={roleId} onChange={(e) => setRoleId(e.target.value)}
              className="w-full bg-main border border-border rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50"
            >
              {roles.filter((r) => !isOwnerRole(r)).map((r) => (
                <option key={r.id} value={r.id} disabled={!isRoleAssignable(r)}>
                  {r.name}{!isRoleAssignable(r) ? ' (em breve)' : ''}
                </option>
              ))}
            </select>
          </div>
          {err && <div className="text-xs text-danger">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-border/50 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">
            Cancelar
          </button>
          <button
            type="submit" disabled={saving || !name || !email || !password || !roleId}
            className="px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent/80 transition-colors disabled:opacity-40"
          >
            {saving ? 'Criando...' : 'Criar usuário'}
          </button>
        </div>
      </form>
    </div>
  );
}
